import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../lib/auth/AuthProvider';
import { supabase } from '../../lib/supabase';
import { useTranslation } from 'react-i18next';
import { FaGithub, FaGoogle, FaApple } from 'react-icons/fa';
import { IOSFormGroup, IOSFormItem, IOSFormInput } from './components/ios/IOSFormGroup';
import { LanguageSwitcher } from '../../components/v0/LanguageSwitcher';
import { trackEvent, identifyUser } from '../../lib/analytics';
import { DocumentViewer } from './components/common/DocumentViewer';
import { getPlatformInfo } from '../../utils/platformUtils';
import { Browser } from '@capacitor/browser';

export const Login: React.FC = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation(['v0/auth', 'v0/common', 'v3/auth']);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [registerLoading, setRegisterLoading] = useState(false);
  // NOTE: 暂时隐藏匿名登录功能相关状态
  // const [anonymousLoading, setAnonymousLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // const [emailExists, setEmailExists] = useState<boolean | null>(null);
  // const [checkingEmail, setCheckingEmail] = useState(false);

  // 文档查看器状态
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);
  const [showTermsOfService, setShowTermsOfService] = useState(false);

  // 注册
  const handleRegister = async () => {
    setRegisterLoading(true);
    setErrorMessage(null);

    // 记录注册尝试事件
    await trackEvent('register attempt', {
      method: 'email',
      user_type: 'regular'
    });

    try {
      const { error, data } = await supabase.auth.signUp({
        email,
        password,
      });

      if (error) {
        setErrorMessage(error.message);
        // 记录注册失败事件
        await trackEvent('register failed', {
          method: 'email',
          user_type: 'regular',
          error: error.message
        });
      } else if (data.user) {
        // 记录注册成功事件
        await trackEvent('user registered', {
          method: 'email',
          user_type: 'regular'
        });

        // 识别用户
        await identifyUser(data.user.id, {
          email: data.user.email,
          user_type: 'regular'
        });
      }
    } catch (err) {
      setErrorMessage(t('v3/auth:login.registerErrorGeneric'));
      // 记录注册异常事件
      await trackEvent('register error', {
        method: 'email',
        user_type: 'regular',
        error: err instanceof Error ? err.message : String(err)
      });
    } finally {
      setRegisterLoading(false);
    }
  };

  // 普通登录
  const handleLogin = async () => {
    setErrorMessage(null);

    // 记录登录尝试事件
    await trackEvent('login attempt', {
      method: 'email',
      user_type: 'regular'
    });

    try {
      const { error, data } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        setErrorMessage(error.message);
        // 记录登录失败事件
        await trackEvent('login failed', {
          method: 'email',
          user_type: 'regular',
          error: error.message
        });
      } else if (data.user) {
        // 记录登录成功事件
        await trackEvent('user logged in', {
          method: 'email',
          user_type: 'regular'
        });

        // 识别用户
        await identifyUser(data.user.id, {
          email: data.user.email,
          user_type: 'regular'
        });
      }
    } catch (err) {
      setErrorMessage(t('v3/auth:login.errorGeneric'));
      // 记录登录异常事件
      await trackEvent('login error', {
        method: 'email',
        user_type: 'regular',
        error: err instanceof Error ? err.message : String(err)
      });
    }
  };

  // 处理社交登录
  const handleSocialLogin = async (provider: 'github' | 'google' | 'apple') => {
    // 记录社交登录尝试事件
    await trackEvent('login attempt', {
      method: provider
    });

    const platformInfo = getPlatformInfo();

    // 调试信息 - 帮助诊断平台检测问题
    console.log('[Login] Platform detection:', {
      isNative: platformInfo.isNative,
      isWeb: platformInfo.isWeb,
      isIOS: platformInfo.isIOS,
      isAndroid: platformInfo.isAndroid,
      os: platformInfo.os,
      userAgent: platformInfo.userAgent
    });

    // 根据平台动态设置重定向 URL
    const getRedirectUrl = () => {
      if (platformInfo.isIOS) {
        return 'ai.motiful.tempo://auth/callback';
      } else if (platformInfo.isAndroid) {
        return 'ai.motiful.tempo://auth/callback';
      } else {
        return `${window.location.origin}/auth/callback`;
      }
    };

    try {
      // 在原生平台使用 in-app browser，Web 平台使用默认行为
      if (platformInfo.isNative) {
        // 获取 OAuth URL
        const { data, error } = await supabase.auth.signInWithOAuth({
          provider,
          options: {
            redirectTo: getRedirectUrl(),
            skipBrowserRedirect: true, // 阻止自动重定向
          },
        });

        if (error) {
          setErrorMessage(error.message);
          await trackEvent('login failed', {
            method: provider,
            error: error.message
          });
          return;
        }

        if (data?.url) {
          // 使用 Capacitor Browser 插件在 app 内打开认证页面
          await Browser.open({
            url: data.url,
            presentationStyle: 'popover', // iOS: 使用 popover 样式
            toolbarColor: '#ffffff',
            windowName: '_self',
          });

          // 记录社交登录重定向事件
          await trackEvent('social login redirect', {
            method: provider,
            mode: 'in_app_browser'
          });
        }
      } else {
        // Web 平台使用默认行为
        const { error } = await supabase.auth.signInWithOAuth({
          provider,
          options: {
            redirectTo: getRedirectUrl(),
          },
        });

        if (error) {
          setErrorMessage(error.message);
          await trackEvent('login failed', {
            method: provider,
            error: error.message
          });
        } else {
          await trackEvent('social login redirect', {
            method: provider,
            mode: 'web_redirect'
          });
        }
      }
    } catch (err) {
      setErrorMessage(t('v3/auth:login.errorGeneric'));
      // 记录社交登录异常事件
      await trackEvent('login error', {
        method: provider,
        error: err instanceof Error ? err.message : String(err)
      });
    }
  };

  // NOTE: 暂时隐藏匿名登录功能
  // 匿名登录处理
  /* const handleAnonymousSignIn = async () => {
    setAnonymousLoading(true);
    setErrorMessage(null);

    try {
      const { data, error } = await supabase.auth.signInAnonymously();

      if (error) {
        setErrorMessage(error.message);
        // 记录匿名登录失败事件
        await trackEvent('login failed', {
          method: 'anonymous',
          user_type: 'anonymous',
          error: error.message
        });
      } else if (data.user) {
        // 记录匿名登录成功事件
        await trackEvent('user logged in', {
          method: 'anonymous',
          user_type: 'anonymous'
        });

        // 识别匿名用户
        await identifyUser(data.user.id, {
          user_type: 'anonymous'
        });
      }
    } catch (err) {
      setErrorMessage(t('v3/auth:login.errorGeneric'));
      // 记录匿名登录异常事件
      await trackEvent('login error', {
        method: 'anonymous',
        user_type: 'anonymous',
        error: err instanceof Error ? err.message : String(err)
      });
    } finally {
      setAnonymousLoading(false);
    }
  }; */

  // 登录成功后重定向
  useEffect(() => {
    if (!loading && user) {
      // 从v3登录页面登录成功后，始终重定向到v3规划页面
      navigate('/v3/chat', { replace: true });
    }
  }, [user, loading, navigate]);

  return (
    <div className="flex flex-col min-h-screen bg-ios-gray6 dark:bg-ios-dark-gray6 font-ios">
      {/* 语言切换器 */}
      <div className="absolute top-4 right-4 z-10">
        <LanguageSwitcher placement="bottom" />
      </div>

      {/* 主内容区域 */}
      <div className="flex flex-1 items-center justify-center p-4">
        <div className="w-full max-w-md">
          {/* Logo和标题 */}
          <div className="flex flex-col items-center mb-8">
            <img
              src="/login-logo.png"
              alt="Tempo Logo"
              className="w-20 h-20 mb-4"
            />
            <h1 className="text-ios-title2 font-semibold text-center dark:text-ios-dark-gray">
              {t('v0/auth:login.welcomeTitle')}
            </h1>
            <p className="text-ios-subhead text-ios-gray dark:text-ios-dark-gray text-center mt-2">
              {t('v0/auth:login.welcomeSubtitle')}
            </p>
          </div>

          {/* 社交登录 - 移到顶部位置 */}
          <div className="mb-6">
            <h2 className="text-ios-headline font-medium text-center mb-4">
              {t('v3/auth:login.quickLoginMethods')}
            </h2>
            <div className="grid grid-cols-3 gap-3">
              <button
                onClick={() => handleSocialLogin('github')}
                className="flex flex-col items-center justify-center py-4 border border-ios-gray5 dark:border-ios-dark-gray4 hover:border-ios-blue dark:hover:border-ios-blue rounded-ios-lg bg-white dark:bg-ios-dark-gray5 hover:bg-ios-gray6 dark:hover:bg-ios-dark-gray4 text-ios-gray dark:text-ios-dark-gray transition-colors duration-200 font-medium"
              >
                <FaGithub className="w-7 h-7 mb-2 text-ios-gray dark:text-ios-dark-gray" />
                <span className="text-ios-subhead">{t('v3/auth:login.providers.github')}</span>
              </button>
              <button
                onClick={() => handleSocialLogin('google')}
                className="flex flex-col items-center justify-center py-4 border border-ios-gray5 dark:border-ios-dark-gray4 hover:border-ios-blue dark:hover:border-ios-blue rounded-ios-lg bg-white dark:bg-ios-dark-gray5 hover:bg-ios-gray6 dark:hover:bg-ios-dark-gray4 text-ios-gray dark:text-ios-dark-gray transition-colors duration-200 font-medium"
              >
                <FaGoogle className="w-7 h-7 mb-2 text-ios-gray dark:text-ios-dark-gray" />
                <span className="text-ios-subhead">{t('v3/auth:login.providers.google')}</span>
              </button>
              <button
                onClick={() => handleSocialLogin('apple')}
                className="flex flex-col items-center justify-center py-4 border border-ios-gray5 dark:border-ios-dark-gray4 hover:border-ios-blue dark:hover:border-ios-blue rounded-ios-lg bg-white dark:bg-ios-dark-gray5 hover:bg-ios-gray6 dark:hover:bg-ios-dark-gray4 text-ios-gray dark:text-ios-dark-gray transition-colors duration-200 font-medium"
              >
                <FaApple className="w-7 h-7 mb-2 text-ios-gray dark:text-ios-dark-gray" />
                <span className="text-ios-subhead">{t('v3/auth:login.providers.apple')}</span>
              </button>
            </div>
          </div>

          {/* 分隔线 - 邮箱登录前 */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-ios-gray5 dark:border-ios-dark-gray5"></div>
            </div>
            <div className="relative flex justify-center text-ios-subhead">
              <span className="px-2 bg-ios-gray6 dark:bg-ios-dark-gray6 text-ios-gray dark:text-ios-dark-gray">
                {t('v3/auth:login.orUseEmail')}
              </span>
            </div>
          </div>

          {/* 邮箱登录表单 - 移到下方位置 */}
          <IOSFormGroup>
            <IOSFormItem>
              <IOSFormInput
                value={email}
                onChange={setEmail}
                placeholder={t('v0/auth:supabase.email_label')}
              />
            </IOSFormItem>
            <IOSFormItem>
              <IOSFormInput
                value={password}
                onChange={setPassword}
                placeholder={t('v0/auth:supabase.password_label')}
                type="password"
                showPasswordToggle={true}
              />
            </IOSFormItem>
          </IOSFormGroup>

          {/* 错误信息 */}
          {errorMessage && (
            <div className="mt-2 text-ios-red dark:text-ios-dark-red text-ios-footnote">
              {errorMessage}
            </div>
          )}

          {/* 注册/登录按钮 */}
          <div className="space-y-3 mt-4 mx-4">
            <button
              onClick={handleRegister}
              disabled={registerLoading}
              className="w-full py-3 bg-ios-blue dark:bg-ios-dark-blue text-white font-medium rounded-ios-lg disabled:opacity-50"
            >
              {registerLoading ? t('v3/auth:login.registerLoading') : t('v3/auth:login.registerButton')}
            </button>
            <button
              onClick={handleLogin}
              className="w-full py-3 border border-ios-gray5 dark:border-ios-dark-gray4 hover:border-ios-blue dark:hover:border-ios-blue rounded-ios-lg bg-white dark:bg-ios-dark-gray5 hover:bg-ios-gray6 dark:hover:bg-ios-dark-gray4 text-ios-gray dark:text-ios-dark-gray font-medium transition-colors duration-200"
            >
              {t('v3/auth:login.loginButton')}
            </button>
          </div>

          {/* NOTE: 暂时隐藏匿名登录入口 */}
          {/* 分隔线 - 匿名登录前 */}
          {/* <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-ios-gray5 dark:border-ios-dark-gray5"></div>
            </div>
            <div className="relative flex justify-center text-ios-subhead">
              <span className="px-2 bg-ios-gray6 dark:bg-ios-dark-gray6 text-ios-gray dark:text-ios-dark-gray">
                或
              </span>
            </div>
          </div> */}

          {/* 匿名登录按钮 */}
          {/* <div className="mx-4 mb-4">
            <button
              onClick={handleAnonymousSignIn}
              disabled={anonymousLoading}
              className="w-full py-3 border border-ios-teal dark:border-ios-dark-teal hover:border-ios-teal dark:hover:border-ios-dark-teal rounded-ios-lg bg-ios-teal/10 dark:bg-ios-dark-teal/20 hover:bg-ios-teal/20 dark:hover:bg-ios-dark-teal/30 text-ios-teal dark:text-ios-dark-teal font-medium transition-colors duration-200 disabled:opacity-50"
            >
              {anonymousLoading ? (
                <div className="flex items-center justify-center">
                  <div className="w-4 h-4 border-2 border-ios-teal dark:border-ios-dark-teal border-t-transparent rounded-full animate-spin mr-2"></div>
                  {t('v3/auth:login.anonymousSignInLoading')}
                </div>
              ) : (
                <div className="flex items-center justify-center">
                  <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M13 10V3L4 14h7v7l9-11h-7z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  {t('v3/auth:login.anonymousSignIn')}
                </div>
              )}
            </button>
            <p className="text-ios-footnote text-ios-gray dark:text-ios-dark-gray text-center mt-2">
              {t('v3/auth:login.anonymousSignInDescription')}
            </p>
          </div> */}

          {/* 隐私政策和服务条款链接 */}
          <div className="mt-4 text-center text-ios-footnote text-ios-gray dark:text-ios-dark-gray">
            {t('v3/auth:login.termsText')}{' '}
            <button
              onClick={() => setShowTermsOfService(true)}
              className="text-ios-blue dark:text-ios-dark-blue bg-transparent border-none p-0 inline font-ios-footnote"
            >
              {t('v3/auth:login.termsLink')}
            </button>{' '}
            {t('v3/auth:login.andText')}{' '}
            <button
              onClick={() => setShowPrivacyPolicy(true)}
              className="text-ios-blue dark:text-ios-dark-blue bg-transparent border-none p-0 inline font-ios-footnote"
            >
              {t('v3/auth:login.privacyLink')}
            </button>
          </div>

          {/* 文档查看器模态框 */}
          <DocumentViewer
            isOpen={showPrivacyPolicy}
            onClose={() => setShowPrivacyPolicy(false)}
            documentUrl="/privacy-policy.html"
            title={t('v3/auth:login.privacyLink')}
          />

          <DocumentViewer
            isOpen={showTermsOfService}
            onClose={() => setShowTermsOfService(false)}
            documentUrl="/terms-of-service.html"
            title={t('v3/auth:login.termsLink')}
          />
        </div>
      </div>
    </div>
  );
};

export default Login;
